import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import StructuredData from 'components/StructuredData';
import {
  structuredOrgData,
  structuredFaq,
} from 'components/StructuredData/structuredDataHelpers';
import React from 'react';
import { isFullUrl } from 'utils/helpers';
import { NO_INDEX_SLUGS } from 'utils/constants/products';
import { FaqSchema } from 'utils/types';
import { JSONLDSchema } from 'components/StructuredData/types';

const config = require('../../utils/constants/siteConfig');

/** Datadog initialization */
if (process.env.NODE_ENV !== 'test') {
  const ddConfig = {
    clientToken: process.env.DATADOG_CLIENT_TOKEN || '',
    site: 'datadoghq.com',
    service: 'www',
    env: process.env.REACT_APP_ENV,
    sessionSampleRate: 100,
    silentMultipleInit: true,
  };

  datadogRum.init({
    ...ddConfig,
    applicationId: process.env.DATADOG_APPLICATION_ID || '',
    sessionReplaySampleRate: 0,
    trackUserInteractions: true,
  });

  datadogLogs.init({
    ...ddConfig,
    forwardErrorsToLogs: true,
    telemetrySampleRate: 0,
  });
}

export interface SeoProps {
  title?: string;
  description?: string;
  keywords?: string;
  url?: string;
  faqSchema?: FaqSchema;
  slug?: string;
  canonicalUrl?: string;
  noIndex?: boolean;
  structuredDataOverride?: Partial<JSONLDSchema> | Partial<JSONLDSchema>[];
}

export const Head = ({
  title,
  description,
  keywords,
  url,
  faqSchema,
  slug,
  canonicalUrl,
  noIndex,
  structuredDataOverride,
}: SeoProps) => {
  const isNoIndex = noIndex || NO_INDEX_SLUGS.includes(slug ?? '');

  // Remove the trailing slash from the canonical URL if it exists
  const seoCanonicalUrl = canonicalUrl?.replace(/\/$/, '');
  // Remove the trailing slash from the URL if it exists, this is used in open graph tags
  const seoUrl = url?.replace(/\/$/, '');
  return (
    <>
      <html lang="en" />
      {/* General tags */}
      <title>{title || config.title}</title>
      <meta
        name="description"
        content={description || config.siteDescription}
      />
      <meta name="keywords" content={keywords || config.keywords} />
      {/* Canonical URL is a Contentful field available in the Pages model.
      If the field is blank, Gastby Helmet plugin will take over and generate the
      Canonical URL based on the page slug.
      */}
      {seoCanonicalUrl && !isFullUrl(seoCanonicalUrl) ? (
        <link
          rel="canonical"
          key={`${config.siteUrl}/${seoCanonicalUrl}/`}
          href={`${config.siteUrl}/${seoCanonicalUrl}/`}
        />
      ) : seoCanonicalUrl ? (
        <link
          rel="canonical"
          key={`${seoCanonicalUrl}/`}
          href={`${seoCanonicalUrl}/`}
        />
      ) : null}
      {/* OpenGraph tags */}
      <meta property="og:title" content={title || config.title} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={seoUrl || config.siteUrl} />
      <meta
        property="og:description"
        content={description || config.siteDescription}
      />
      {/* TODO: Add og:image meta tag dynamically */}
      {canonicalUrl === '360-full-body-test' && (
        <meta
          property="og:image"
          content="https://images.ctfassets.net/ydyvnem5zkxh/4nxQS4XKyPtTgXlB3aCjjz/c68df5bde7c6e29f4879815a47c0024a/Center_3_mobile.png"
        />
      )}
      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta
        name="twitter:creator"
        content={config.userTwitter ? config.userTwitter : ''}
      />
      <meta name="twitter:title" content={title || config.siteTitle} />
      <meta
        name="twitter:description"
        content={description || config.siteDescription}
      />
      {/* Disable iPhone auto zoom on select drop down menus */}
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, maximum-scale=1"
      />
      {isNoIndex ? (
        <meta name="robots" content="noindex, nofollow, noimageindex" />
      ) : null}
      {/* Schema.org tags */}
      {/* Organization Schema Attributes from config */}
      {structuredDataOverride ? (
        <script type="application/ld+json">
          {JSON.stringify(structuredDataOverride)}
        </script>
      ) : (
        <script type="application/ld+json">
          {JSON.stringify(structuredOrgData)}
        </script>
      )}
      {/* FAQ Schema Attributes from Contentful */}
      {faqSchema?.length ? (
        <script type="application/ld+json">
          {JSON.stringify(structuredFaq(faqSchema))}
        </script>
      ) : null}
    </>
  );
};

// Legacy SEO component for backward compatibility
export const SEO = ({
  title,
  description,
  keywords,
  url,
  faqSchema,
  slug,
  canonicalUrl,
  noIndex,
  structuredDataOverride,
}: SeoProps): JSX.Element => {
  return (
    <>
      <Head
        title={title}
        description={description}
        keywords={keywords}
        url={url}
        faqSchema={faqSchema}
        slug={slug}
        canonicalUrl={canonicalUrl}
        noIndex={noIndex}
        structuredDataOverride={structuredDataOverride}
      />
    </>
  );
};
